import type { ConversationStatusEnumKey } from '../../../core/api/generated/models/ConversationStatus';
import { slotKey } from './sla-target-slots';

/** Metas da política ativa, indexadas por `metric|track` — mesma chave de `SLA_TARGET_SLOTS`. */
export type SlaPolicyTargets = Readonly<Record<string, number>>;

export interface SlaConversationInput {
  firstResponseAt?: string | null;
  status: ConversationStatusEnumKey;
  /** Início do turno aberto — a mesma âncora que o motor usa. Null = ninguém esperando. */
  awaitingReplySince?: string | null;
  /** Quem está na linha para responder o turno aberto. */
  awaitingTrack?: 'human' | 'ai' | null;
  /** Reabertura da conversa pausada; alimenta o selo "Pausado". */
  snoozedUntil?: string | null;
}

export type SlaTone = 'ok' | 'warning' | 'danger' | 'paused';

export interface SlaBadge {
  label: string;
  tone: SlaTone;
}

const TERMINAL_STATUSES = new Set<ConversationStatusEnumKey>(['resolved', 'archived', 'channel_unavailable']);
const WARNING_RATIO = 0.2; // dentro dos últimos 20% da janela → atenção

/**
 * Selo de SLA da conversa.
 *
 * Usa a MESMA âncora e a MESMA meta que o motor de turno do backend: o relógio começa em
 * `awaitingReplySince` (não na criação da conversa nem no início da sessão) e a meta sai da
 * combinação (métrica, track) do turno aberto. Antes o selo media desde `sessionStartedAt` contra
 * uma meta única sem track, enquanto o painel media outra coisa — os dois discordavam e não dava
 * para confiar em nenhum.
 *
 * LIMITE CONHECIDO: aqui o tempo é corrido; o motor conta em horário comercial. Numa conversa que
 * atravessa a noite ou o fim de semana o selo fica mais pessimista que o painel. Alinhar exigiria o
 * backend devolver os segundos comerciais decorridos junto da conversa — replicar o calendário no
 * cliente duplicaria o motor. A divergência que sobra é só de relógio: âncora e meta já são as
 * mesmas, então os dois concordam sobre QUAL turno está aberto e contra QUAL alvo ele corre.
 */
export function computeSlaBadge(
  conversation: SlaConversationInput,
  targets: SlaPolicyTargets | null,
  now: Date = new Date(),
): SlaBadge | null {
  if (conversation.status === 'snoozed') {
    if (!conversation.snoozedUntil) return { label: 'Pausado', tone: 'paused' };
    const remainingSeconds = (new Date(conversation.snoozedUntil).getTime() - now.getTime()) / 1000;
    if (remainingSeconds <= 0) return { label: 'Pausado', tone: 'paused' };
    return { label: `Pausado · ${formatRemaining(remainingSeconds)}`, tone: 'paused' };
  }
  if (!targets || TERMINAL_STATUSES.has(conversation.status)) return null;

  // Sem turno aberto ninguém está esperando por nós — não há SLA correndo.
  if (!conversation.awaitingReplySince) return null;

  const metric = conversation.firstResponseAt ? 'next_reply' : 'first_reply';
  const track = conversation.awaitingTrack ?? 'human';
  const targetSeconds = targets[slotKey(metric, track)];
  // Combinação sem meta configurada: o tempo segue sendo medido no painel, mas não há prazo a exibir.
  if (targetSeconds === undefined) return null;

  const kind = metric === 'first_reply' ? '1ª resposta' : 'resposta';
  const elapsedSeconds = (now.getTime() - new Date(conversation.awaitingReplySince).getTime()) / 1000;
  const remainingSeconds = targetSeconds - elapsedSeconds;

  if (remainingSeconds <= 0) return { label: `${kind}: estourado`, tone: 'danger' };

  const tone: SlaTone = remainingSeconds <= targetSeconds * WARNING_RATIO ? 'warning' : 'ok';
  return { label: `${kind}: ${formatRemaining(remainingSeconds)}`, tone };
}

function formatRemaining(seconds: number): string {
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
